/**
 * SwissPass OAuth return landing page (`/login/swisspass/*`).
 *
 * Handles all SwissPass deep-link returns outside {@link IonRouterOutlet}
 * to avoid the cold-load stack-router issue observed for OAuth callbacks.
 *
 * - `/login/swisspass/callback` renders the callback exchange page.
 * - `/login/swisspass/canceled_after_registration` redirects to `/home`
 *   with a transient state flag so the registration confirmation popup appears.
 * - `/login/swisspass/ckm_link_update_finished` and
 *   `/login/swisspass/login_update_finished` redirect to `/home` and trigger
 *   a silent background provider profile refresh.
 * - Any other `/login/swisspass/*` suffix redirects to `/home`.
 */

import React, { useEffect, useRef } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { updateProviderProfile } from "../../services/auth";
import SwissPassCallbackPage from "./SwissPassCallbackPage";

/** Exact callback path for the SwissPass authorization-code return. */
const SWISSPASS_CALLBACK_PATH = "/login/swisspass/callback";

/**
 * Path used by SwissPass when a user created an account but still needs
 * to complete the FlowRide login.
 */
const SWISSPASS_CANCELED_AFTER_REGISTRATION_PATH = "/login/swisspass/canceled_after_registration";

/** Path used by SwissPass when the customer portal link update is finished. */
const SWISSPASS_CKM_LINK_UPDATE_FINISHED_PATH = "/login/swisspass/ckm_link_update_finished";

/** Path used by SwissPass when the login data update is finished. */
const SWISSPASS_LOGIN_UPDATE_FINISHED_PATH = "/login/swisspass/login_update_finished";

/**
 * Strips a trailing slash from a path so the exact comparisons below
 * accept both `/login/swisspass/callback` and `/login/swisspass/callback/`.
 *
 * @param path - Current location pathname.
 * @returns Pathname without a trailing slash.
 */
function normalizePath( path: string ): string {
    return path.replace( /\/+$/, "" );
}

/**
 * Checks whether the current path is one of the provider profile update
 * finished return paths.
 *
 * @param path - Normalized location pathname.
 * @returns True when the path is a provider update finished suffix.
 */
function isProviderUpdateFinishedPath( path: string ): boolean {
    const normalized = normalizePath( path );

    return normalized === normalizePath( SWISSPASS_CKM_LINK_UPDATE_FINISHED_PATH ) ||
      normalized === normalizePath( SWISSPASS_LOGIN_UPDATE_FINISHED_PATH );
}

const SwissPassReturnPage: React.FC = () => {
    const location = useLocation();
    const history = useHistory();
    const normalizedPath = normalizePath( location.pathname );

    /*
     * Prevents the background call from being triggered twice in development
     * (React Strict Mode) or if the location changes before unmount.
     */
    const updateStartedRef = useRef( false );

    useEffect( () => {
        if ( updateStartedRef.current || !isProviderUpdateFinishedPath( normalizedPath ) ) {
            return;
        }
        updateStartedRef.current = true;

        /**
         * Trigger a silent background provider profile refresh and swallow
         * any error so the redirect stays transparent for the user.
         */
        void ( async() => {
            try {
                await updateProviderProfile( "swisspass" );
            } catch {
                // Background refresh is best-effort; no user feedback needed.
            }
        } )();

        history.replace( "/home" );
    }, [ history, normalizedPath ] );

    if ( normalizedPath === normalizePath( SWISSPASS_CALLBACK_PATH ) ) {
        return <SwissPassCallbackPage />;
    }

    if ( normalizedPath === normalizePath( SWISSPASS_CANCELED_AFTER_REGISTRATION_PATH ) ) {
        return (
            <Redirect
                to={{
                    "pathname": "/home",
                    "state":    { "showSwissPassPostRegistration": true },
                }}
            />
        );
    }

    /*
     * Provider update-finished paths render nothing while the effect
     * immediately redirects to /home in the background.
     */
    if ( isProviderUpdateFinishedPath( normalizedPath ) ) {
        return null;
    }

    // All other SwissPass return suffixes fall back to the normal application.
    return <Redirect to="/home" />;
};

export default SwissPassReturnPage;
